import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ShieldCheck, Trash2, Pencil, Layers, Users2, CalendarDays } from 'lucide-react';
import { api } from '../api';
import { PageLoading, Modal, useToast, StatusPill, Confirm } from '../components/ui';

const newPart = (i) => ({ label: `Teil ${i + 1}`, date: '', end_date: '', _overnight: false, start_time: '', end_time: '', total_slots: 6, requires_gf: true, requires_ma: true, ma_requires_ce: false, min_as: 0 });
const emptyWatch = () => ({ title: '', location: '', description: '', vehicle: '', status: 'planned', parts: [newPart(0)] });
const addDay = (d) => { if (!d) return ''; const dt = new Date(d + 'T00:00:00Z'); if (isNaN(dt)) return ''; dt.setUTCDate(dt.getUTCDate() + 1); return dt.toISOString().slice(0, 10); };

export default function Watches() {
  const toast = useToast();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/watches').then((d) => setList(d.watches));
  useEffect(() => { load(); }, []);
  if (!list) return <PageLoading />;

  const openEdit = async (w) => {
    if (!w) return setEdit(emptyWatch());
    const full = await api.get(`/watches/${w.id}`);
    setEdit({
      id: full.watch.id, title: full.watch.title, location: full.watch.location || '', description: full.watch.description || '', vehicle: full.watch.vehicle || '',
      status: full.watch.status,
      parts: full.parts.map((p) => ({ id: p.id, label: p.label, date: p.date || '', end_date: p.end_date || '', _overnight: !!p.end_date, start_time: p.start_time || '', end_time: p.end_time || '', total_slots: p.total_slots, requires_gf: !!p.requires_gf, requires_ma: !!p.requires_ma, ma_requires_ce: !!p.ma_requires_ce, min_as: p.min_as })),
    });
  };

  const setPart = (idx, patch) => setEdit((prev) => ({ ...prev, parts: prev.parts.map((p, i) => i === idx ? { ...p, ...patch } : p) }));
  const addPart = () => setEdit((prev) => ({ ...prev, parts: [...prev.parts, newPart(prev.parts.length)] }));
  const rmPart = (idx) => setEdit((prev) => ({ ...prev, parts: prev.parts.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!edit.title) return toast('Titel erforderlich', 'err');
    if (!edit.parts.length) return toast('Mindestens ein Teil erforderlich', 'err');
    for (const p of edit.parts) {
      if (p._overnight && (!p.end_date || (p.date && p.end_date <= p.date)))
        return toast('Bei „Wache endet am anderen Tag" bitte ein Enddatum nach dem Startdatum angeben', 'err');
    }
    setSaving(true);
    try {
      let id = edit.id;
      if (id) await api.put(`/watches/${id}`, edit);
      else { const r = await api.post('/watches', edit); id = r.id; }
      setEdit(null); load(); toast('Gespeichert');
      if (!edit.id) navigate(`/wachen/${id}`);
    } catch (e) { toast(e.message, 'err'); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.del(`/watches/${id}`); load(); toast('Gelöscht'); } catch (e) { toast(e.message, 'err'); } };

  const multi = edit && edit.parts.length > 1;

  return (
    <>
      <div className="page-head">
        <div><h1>Sicherheitswachen</h1><div className="sub">Wachen anlegen, Bewerber wählen und einteilen. Vergangene Wachen hier als „abgeschlossen" anlegen (für die Fairness).</div></div>
        <div className="toolbar"><button className="btn btn-primary" onClick={() => openEdit(null)}><Plus size={16} /> Neue Wache</button></div>
      </div>

      <div className="card">
        {list.length === 0 ? <div className="empty"><ShieldCheck size={40} /><p>Noch keine Wachen angelegt.</p></div> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Wache</th><th>Datum</th><th>Teile</th><th>Plätze</th><th>Bewerber</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {list.map((w) => (
                  <tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/wachen/${w.id}`)}>
                    <td><strong>{w.title}</strong>{w.location && <div className="muted xsmall">{w.location}</div>}</td>
                    <td className="small">{w.firstDate ? (w.firstDate === w.lastDate ? w.firstDate : `${w.firstDate} – ${w.lastDate}`) : '—'}</td>
                    <td>{w.partsCount > 1 ? <span className="badge badge-gray"><Layers size={11} /> {w.partsCount}</span> : <span className="muted">—</span>}</td>
                    <td className="small">{w.filled}/{w.totalSlots}</td>
                    <td><span className="badge badge-gray"><Users2 size={11} /> {w.applicants}</span></td>
                    <td><StatusPill status={w.status} />{w.released ? <span className="badge badge-green" style={{ marginLeft: 4 }}>freigegeben</span> : null}</td>
                    <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(w)}><Pencil size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDel(w)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {edit && (
        <Modal large title={edit.id ? 'Wache bearbeiten' : 'Neue Sicherheitswache'} onClose={() => setEdit(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setEdit(null)}>Abbrechen</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button></>}>
          <div className="form-row">
            <div className="form-group"><label>Titel</label><input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="z. B. Stadtfest 2026" /></div>
            <div className="form-group"><label>Ort</label><input value={edit.location} onChange={(e) => setEdit({ ...edit, location: e.target.value })} /></div>
          </div>
          <div className="form-group"><label>Fahrzeug (optional)</label><input value={edit.vehicle} onChange={(e) => setEdit({ ...edit, vehicle: e.target.value })} placeholder="z. B. 1/11/2" /><span className="muted xsmall">Wird bei der Einteilungs-Freigabe in die FF-Agent-Zeile „Fahrzeug (…) habe ich euch reserviert" übernommen.</span></div>
          <div className="form-group"><label>Beschreibung</label><textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
          <div className="form-group">
            <label>Status</label>
            <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
              <option value="planned">Geplant</option>
              <option value="draft">Entwurf</option>
              <option value="assigned">Eingeteilt</option>
              <option value="completed">Abgeschlossen (vergangene Wache)</option>
              <option value="cancelled">Storniert</option>
            </select>
            <span className="muted xsmall">„Abgeschlossen" zählt für die Fairness (Vorbelastung). So legst du vergangene Wachen an.</span>
          </div>

          <div className="divider" />
          <div className="flex between"><label style={{ fontSize: 14 }}><CalendarDays size={14} style={{ verticalAlign: -2 }} /> Teile / Schichten</label><button className="btn btn-secondary btn-sm" onClick={addPart}><Plus size={14} /> Teil hinzufügen</button></div>

          {edit.parts.map((p, idx) => (
            <div key={idx} className="card card-pad" style={{ boxShadow: 'none', background: 'var(--bg-tertiary)' }}>
              <div className="flex between" style={{ marginBottom: 10 }}>
                {multi
                  ? <input value={p.label} onChange={(e) => setPart(idx, { label: e.target.value })} style={{ maxWidth: 220, fontWeight: 600 }} />
                  : <strong>Details</strong>}
                {edit.parts.length > 1 && <button className="btn btn-ghost btn-sm" onClick={() => rmPart(idx)}><Trash2 size={14} /></button>}
              </div>
              <div className="form-row">
                <div className="form-group"><label>Datum</label><input type="date" value={p.date} onChange={(e) => { const nd = e.target.value; setPart(idx, { date: nd, ...(p._overnight ? { end_date: (p.end_date && p.end_date > nd) ? p.end_date : addDay(nd) } : {}) }); }} /></div>
                <div className="form-group"><label>Plätze gesamt (inkl. GF/Ma)</label><input type="number" min={1} value={p.total_slots} onChange={(e) => setPart(idx, { total_slots: Number(e.target.value) })} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Beginn</label><input type="time" value={p.start_time} onChange={(e) => setPart(idx, { start_time: e.target.value })} /></div>
                <div className="form-group"><label>Ende</label><input type="time" value={p.end_time} onChange={(e) => setPart(idx, { end_time: e.target.value })} /></div>
              </div>
              <div className="flex flex-wrap" style={{ gap: 16, marginTop: 4, marginBottom: 4 }}>
                <label className="checkbox-row"><input type="checkbox" checked={!!p._overnight}
                  onChange={(e) => { const on = e.target.checked; setPart(idx, { _overnight: on, end_date: on ? (p.date ? addDay(p.date) : '') : '' }); }} /> Wache endet am anderen Tag</label>
                {p._overnight && (
                  <div className="form-group" style={{ minWidth: 170 }}><label>Enddatum</label>
                    <input type="date" value={p.end_date} min={p.date || undefined} onChange={(e) => setPart(idx, { end_date: e.target.value })} />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap" style={{ gap: 16, marginTop: 4 }}>
                <label className="checkbox-row"><input type="checkbox" checked={p.requires_gf} onChange={(e) => setPart(idx, { requires_gf: e.target.checked })} /> Wachführer (GF) nötig</label>
                <label className="checkbox-row"><input type="checkbox" checked={p.requires_ma} onChange={(e) => setPart(idx, { requires_ma: e.target.checked })} /> Maschinist (Ma) nötig</label>
                {p.requires_ma && <label className="checkbox-row"><input type="checkbox" checked={p.ma_requires_ce} onChange={(e) => setPart(idx, { ma_requires_ce: e.target.checked })} /> Ma braucht CE</label>}
                <label className="checkbox-row" title="Mindestanzahl Atemschutzgeräteträger in der Mannschaft">AS-Pflicht (min.):
                  <input type="number" min={0} value={p.min_as} onChange={(e) => setPart(idx, { min_as: Number(e.target.value) })} style={{ width: 64 }} />
                </label>
              </div>
            </div>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={addPart} style={{ alignSelf: 'flex-start' }}><Plus size={14} /> Weiteren Teil hinzufügen</button>
          {edit.parts.length === 1 && <span className="muted xsmall">Mehrteilige Wache? Über „Teil hinzufügen" weitere Schichten anlegen – dann können Bewerber pro Teil eingeteilt werden und einen Wunsch-Teil angeben.</span>}
        </Modal>
      )}
      {del && <Confirm text={`Wache „${del.title}" inkl. Teile, Bewerber und Einteilung löschen?`} onConfirm={() => remove(del.id)} onClose={() => setDel(null)} />}
    </>
  );
}
